import { useRef, useState } from 'react';
import type { DemoKey, DemoScript, FingerKey, Fx, Level, RoundProps, ViewProps } from '../kit.js';
import { BAD, BOARD, BoardFrame, OK, clamp, easier, q, say, seeded, thirtyGame, useRound, useSvgId } from '../kit.js';
import { ERA_SKIN } from '../../art.js';
import type { Era } from '../../art.js';
import { SCATTER, ShipSide } from './art.js';

/**
 * NAVIGATOR · FLY THE GAPS — one touch lifts the ship, gravity pulls; fly through the gaps.
 *
 * The classic THREAD as three short flights of three or four gates, each gate a structure of the
 * era: a station's truss with a ring round the opening, tower blocks with neon signs, torn hull
 * plates, tunnel rock, or the forest's canopy. Round 1 has wide openings, slow gates and each
 * opening near the last; each round after has narrower openings, a little faster, a little further
 * apart. Nothing moves until the first tap, and with no tap for three seconds the gates wait. The
 * top and bottom only stop the ship. Clipping a gate is the mistake; the gate still counts as flown.
 */

export interface GapsLevel {
  gates: number;
  /** The opening, as a fraction of the board's height. */
  gap: number;
  /** How fast the gates come, in board widths a second. */
  speed: number;
  /** Between one gate and the next, in board widths. */
  spacing: number;
  /** How far one opening may sit from the last, in board heights. */
  drift: number;
}

/** Easier since 6 Oct 2026 (kit `easier`): round 1 as before, round 2 halfway to the old round 2, round 3 the old round 2. */
export const GAPS_LEVELS: readonly GapsLevel[] = easier([
  { gates: 3, gap: 0.42, speed: 0.25, spacing: 0.62, drift: 0.14 },
  { gates: 4, gap: 0.35, speed: 0.29, spacing: 0.58, drift: 0.2 },
]);

/** The flight, in board heights (y grows downwards) and seconds. */
export const FLIGHT = {
  gravity: 2.1,
  /** A tap sets the climb to this. */
  lift: 0.8,
  /** It never sinks faster than this. */
  maxFall: 0.55,
  /** Where the ship flies, across the board. */
  shipX: 0.22,
  /** Where the first gate waits before the first tap. */
  firstGateX: 0.82,
  /** No tap for this long and the gates wait for the guest. */
  restMs: 3_000,
  /** Half the ship's height: nearer a wall than this is clipping it. A little under the drawing. */
  shipHalf: 0.022,
} as const;

/** The drawn gate's width and the ship's half length, in board units. */
const GATE_W = 42;
const SHIP_HALF_LEN = 14;
const W = BOARD.w;
const H = BOARD.h;
/** A gate is level with the ship while it is this close across, as a share of the board. */
const REACH = (GATE_W / 2 + SHIP_HALF_LEN) / W;

export interface Ship {
  y: number;
  vel: number;
}

/** One frame of flight: it sinks, never faster than a drift, and the top and bottom stop it. */
export function stepShip(s: Ship, dt: number): void {
  s.vel = Math.min(FLIGHT.maxFall, s.vel + FLIGHT.gravity * dt);
  s.y += s.vel * dt;
  if (s.y < 0.05) {
    s.y = 0.05;
    s.vel = Math.max(0, s.vel);
  }
  if (s.y > 0.95) {
    s.y = 0.95;
    s.vel = 0;
  }
}

/** A tap: the ship climbs. */
export function lift(s: Ship): void {
  s.vel = -FLIGHT.lift;
}

/** Is a ship at `y` touching a wall of the gate whose opening is centred on `centre`? */
export function clipsGate(y: number, centre: number, gap: number): boolean {
  return Math.abs(y - centre) > gap / 2 - FLIGHT.shipHalf;
}

/** Where each opening sits: the first near the middle, where the ship starts; each next a short climb or drop away. */
export function gapCentres(level: Level, rand: () => number): number[] {
  const L = GAPS_LEVELS[level]!;
  const lo = L.gap / 2 + 0.07;
  const hi = 1 - L.gap / 2 - 0.07;
  const out: number[] = [];
  let c = 0.5;
  for (let i = 0; i < L.gates; i++) {
    if (i === 0) c = 0.5 + (rand() - 0.5) * 0.08;
    else {
      const move = L.drift * (0.55 + 0.45 * rand());
      let dir = rand() < 0.5 ? -1 : 1;
      if (c + dir * move > hi || c + dir * move < lo) dir = -dir;
      c = clamp(c + dir * move, lo, hi);
    }
    out.push(c);
  }
  return out;
}

export interface Gate {
  /** Across the board, as a share of its width. */
  x: number;
  /** The opening's middle, as a share of its height. */
  c: number;
  passed: boolean;
  hit: boolean;
}

/** The gates of a round, lined up from where the first waits. */
export function lineUp(level: Level, rand: () => number): Gate[] {
  const L = GAPS_LEVELS[level]!;
  return gapCentres(level, rand).map((c, i) => ({ x: FLIGHT.firstGateX + i * L.spacing, c, passed: false, hit: false }));
}

/**
 * The gates move on by `dx` (a share of the width) past a ship at `y`: how many it clips, how many
 * it has flown through, and of those how many without touching.
 */
export function passGates(gates: Gate[], dx: number, y: number, gap: number): { clipped: number; flown: number; clean: number } {
  let clipped = 0;
  let flown = 0;
  let clean = 0;
  for (const g of gates) g.x -= dx;
  for (const g of gates) {
    if (g.passed) continue;
    const off = g.x - FLIGHT.shipX;
    if (!g.hit && Math.abs(off) < REACH && clipsGate(y, g.c, gap)) {
      g.hit = true;
      clipped++;
    }
    if (off < -REACH) {
      g.passed = true;
      flown++;
      if (!g.hit) clean++;
    }
  }
  return { clipped, flown, clean };
}

/** Everything the flight shows. */
export interface GapsView {
  /** The game clock, ms. */
  t: number;
  /** The ship: its height (units from the top), tilt (degrees), burn (0–1) and the shield after a clip (0–1). */
  y: number;
  a: number;
  b: number;
  sh: number;
  /** The gates in sight: across (units), the opening's middle (units), and 0 ahead, 1 flown clean, 2 clipped. */
  g: readonly (readonly [x: number, c: number, s: 0 | 1 | 2])[];
  /** The opening's height, units. */
  gap: number;
  /** How far the flight has come, units: the scenery scrolls by it. */
  f: number;
  /** Gates flown, and how many in all. */
  n: number;
  of: number;
  /** 0 waiting for the first tap, 1 flying, 2 waiting for a tap to fly on, 3 through. */
  s: 0 | 1 | 2 | 3;
  fx?: Fx | undefined;
}

/** A zigzag of bracing between two x's, from y0 to y1. */
function zigzag(xa: number, xb: number, y0: number, y1: number, step: number): string {
  if (y1 - y0 < step) return '';
  let d = `M${xa} ${y0}`;
  let left = false;
  for (let y = y0 + step; y <= y1; y += step) {
    d += ` L${left ? xa : xb} ${y}`;
    left = !left;
  }
  return d;
}

/** The far layer, one board wide, drawn twice side by side as it scrolls. */
function farPath(era: Era): string {
  let d = '';
  if (era === 'neon') {
    for (let x = 0, i = 0; x < W; x += 26, i++) {
      const h = 34 + ((i * 37) % 52);
      d += `M${x} ${H} L${x} ${H - h} L${x + 22} ${H - h} L${x + 22} ${H} Z `;
    }
  } else if (era === 'dust') {
    // The tunnel's roof and floor, rough.
    d += `M0 0 L${W} 0 L${W} 10`;
    for (let x = W; x >= 0; x -= 20) d += ` L${x} ${8 + ((x * 7) % 11)}`;
    d += ` Z M0 ${H} L${W} ${H} L${W} ${H - 10}`;
    for (let x = W; x >= 0; x -= 24) d += ` L${x} ${H - 9 - ((x * 5) % 13)}`;
    d += ' Z';
  } else if (era === 'canopy') {
    d += `M0 ${H}`;
    for (let x = 0; x <= W; x += 18) d += ` L${x} ${H - 26 - ((x * 13) % 22)} L${x + 9} ${H - 34 - ((x * 7) % 20)}`;
    d += ` L${W} ${H} Z`;
  }
  return d;
}
const FAR: Record<Era, string> = { stars: '', neon: farPath('neon'), debris: '', dust: farPath('dust'), canopy: farPath('canopy') };

function GateArt({ era, id, x, top, bot, lip, bright }: { era: Era; id: string; x: number; top: number; bot: number; lip: string; bright: boolean }) {
  const skin = ERA_SKIN[era];
  const mid = (top + bot) / 2;
  const topLen = top;
  const botLen = H - bot;
  if (era === 'neon') {
    return (
      <g transform={`translate(${x} 0)`}>
        <rect x="-19" y="-4" width="38" height={Math.max(0, top - 8)} fill={`url(#${id}s)`} stroke={skin.ink} strokeOpacity=".35" />
        <rect x="-19" y="-4" width="38" height={Math.max(0, top - 16)} fill={`url(#${id}p)`} />
        <rect x="-22" y={top - 13} width="44" height="12" rx="3" fill="none" stroke={skin.ink} strokeWidth="6" opacity=".2" />
        <rect x="-22" y={top - 13} width="44" height="12" rx="3" fill="#12071f" stroke={bright ? lip : skin.ink} strokeWidth="2" />
        <path d={`M-14 ${top - 7} h7 m4 0 h9 m4 0 h6`} stroke={skin.haze} strokeWidth="2.2" strokeLinecap="round" />
        <rect x="-19" y={bot + 8} width="38" height={Math.max(0, botLen - 4)} fill={`url(#${id}s)`} stroke={skin.haze} strokeOpacity=".3" />
        <rect x="-19" y={bot + 16} width="38" height={Math.max(0, botLen - 12)} fill={`url(#${id}p)`} />
        <rect x="-22" y={bot + 1} width="44" height="12" rx="3" fill="none" stroke={skin.haze} strokeWidth="6" opacity=".2" />
        <rect x="-22" y={bot + 1} width="44" height="12" rx="3" fill="#07131f" stroke={bright ? lip : skin.haze} strokeWidth="2" />
        <path d={`M-13 ${bot + 7} h10 m4 0 h5 m4 0 h7`} stroke={skin.ink} strokeWidth="2.2" strokeLinecap="round" />
      </g>
    );
  }
  if (era === 'debris') {
    // Torn plates: the edge is ragged, and its lowest points are the wall.
    const edgeTop = `M19 ${top - 4} L13 ${top} L6 ${top - 4} L-1 ${top - 1} L-8 ${top - 4} L-14 ${top} L-19 ${top - 3}`;
    const edgeBot = `M19 ${bot + 3} L12 ${bot} L5 ${bot + 4} L-2 ${bot + 1} L-9 ${bot + 4} L-15 ${bot} L-19 ${bot + 4}`;
    return (
      <g transform={`translate(${x} 0)`}>
        {topLen > 70 && <path d={`M18 ${top * 0.45} l12 10`} stroke="#5b3434" strokeWidth="3.4" strokeLinecap="round" />}
        <path d={`M-19 -4 L19 -4 ${edgeTop.replace('M', 'L')} Z`} fill={`url(#${id}w)`} stroke="#000" strokeOpacity=".5" />
        {topLen > 40 && <rect x="-19" y={top - 30} width="38" height="9" fill={`url(#${id}z)`} opacity=".75" />}
        {topLen > 70 && <path d={`M-19 ${top - 52} L19 ${top - 52}`} stroke="#000" strokeOpacity=".35" strokeWidth="1.5" />}
        <path d={edgeTop} stroke={skin.haze} strokeWidth="6" opacity=".2" fill="none" />
        <path d={edgeTop} stroke={bright ? lip : skin.haze} strokeWidth="2" fill="none" />
        <path d={`M-19 ${H + 4} L19 ${H + 4} ${edgeBot.replace('M', 'L')} Z`} fill={`url(#${id}w)`} stroke="#000" strokeOpacity=".5" />
        {botLen > 40 && <rect x="-19" y={bot + 21} width="38" height="9" fill={`url(#${id}z)`} opacity=".75" />}
        {botLen > 70 && <path d={`M-19 ${bot + 50} L19 ${bot + 50}`} stroke="#000" strokeOpacity=".35" strokeWidth="1.5" />}
        <path d={edgeBot} stroke={skin.haze} strokeWidth="6" opacity=".2" fill="none" />
        <path d={edgeBot} stroke={bright ? lip : skin.haze} strokeWidth="2" fill="none" />
      </g>
    );
  }
  if (era === 'dust') {
    return (
      <g transform={`translate(${x} 0)`}>
        <path
          d={`M-24 -4 L24 -4 L22 ${top - 16} Q 18 ${top - 1} 8 ${top} Q -2 ${top + 1} -11 ${top - 2} Q -20 ${top - 5} -22 ${top - 18} Z`}
          fill={`url(#${id}r)`}
          stroke="#1a0f05"
          strokeOpacity=".6"
        />
        {topLen > 50 && <path d={`M-22 ${top * 0.4} Q 0 ${top * 0.4 + 6} 22 ${top * 0.4 - 3}`} stroke="#000" strokeOpacity=".25" fill="none" />}
        {topLen > 90 && <path d={`M-22 ${top * 0.72} Q 2 ${top * 0.72 - 5} 22 ${top * 0.72 + 2}`} stroke="#000" strokeOpacity=".22" fill="none" />}
        <path d={`M-12 ${top - 13} l0 -5`} stroke="#3a2a18" strokeWidth="2" />
        <circle cx="-12" cy={top - 9} r="7" fill={bright ? lip : '#ffc46b'} opacity=".22" />
        <circle cx="-12" cy={top - 9} r="2.6" fill={bright ? lip : '#ffe0a6'} />
        <path
          d={`M-24 ${H + 4} L-22 ${bot + 17} Q -18 ${bot + 2} -8 ${bot} Q 2 ${bot - 1} 10 ${bot + 2} Q 20 ${bot + 5} 22 ${bot + 18} L24 ${H + 4} Z`}
          fill={`url(#${id}r)`}
          stroke="#1a0f05"
          strokeOpacity=".6"
        />
        {botLen > 50 && <path d={`M-22 ${bot + botLen * 0.5} Q 0 ${bot + botLen * 0.5 - 6} 22 ${bot + botLen * 0.5 + 3}`} stroke="#000" strokeOpacity=".25" fill="none" />}
        <circle cx="12" cy={bot + 9} r="7" fill={bright ? lip : '#ffc46b'} opacity=".22" />
        <circle cx="12" cy={bot + 9} r="2.6" fill={bright ? lip : '#ffe0a6'} />
      </g>
    );
  }
  if (era === 'canopy') {
    // The lip: a row of leaf clumps, lit from above.
    const lipLeaves = (y: number, flip: number) =>
      [-15, -5, 5, 15].map((lx, i) => (
        <g key={lx}>
          <circle cx={lx} cy={y} r={i % 2 ? 9 : 8} fill={i % 2 ? '#4f9440' : '#3d7a32'} />
          <circle cx={lx - 2} cy={y - 3 * flip} r="3.2" fill="#b4e88e" opacity=".5" />
        </g>
      ));
    // Down the column: clusters on alternate sides, so it reads as foliage, not a pipe.
    const clusters = (y0: number, y1: number) => {
      const out = [];
      for (let y = y0, k = 0; y < y1; y += 19, k++) {
        const side = k % 2 ? 1 : -1;
        out.push(
          <g key={y}>
            <circle cx={side * 13} cy={y} r="9.5" fill={k % 3 ? '#2f6a2a' : '#3b7d33'} />
            <circle cx={-side * 9} cy={y + 9} r="7" fill="#285c24" />
            <circle cx={side * 15} cy={y - 3} r="2.8" fill="#9fdc7a" opacity=".35" />
          </g>,
        );
      }
      return out;
    };
    return (
      <g transform={`translate(${x} 0)`}>
        <rect x="-19" y="-4" width="38" height={Math.max(0, top - 8)} fill={`url(#${id}l)`} />
        {clusters(6, top - 20)}
        <path d={`M-9 -4 Q -12 ${top * 0.5} -8 ${top - 12}`} stroke="#6b4a2a" strokeWidth="1.8" fill="none" opacity=".8" />
        <path d={`M10 -4 Q 13 ${top * 0.4} 9 ${top - 16}`} stroke="#7cc25e" strokeWidth="1.2" fill="none" opacity=".6" />
        {lipLeaves(top - 8, 1)}
        {bright && <path d={`M-18 ${top + 0.5} L18 ${top + 0.5}`} stroke={lip} strokeWidth="1.6" opacity=".9" />}
        <rect x="-19" y={bot + 8} width="38" height={Math.max(0, botLen)} fill={`url(#${id}l)`} />
        <rect x="-5" y={bot + 8} width="10" height={Math.max(0, botLen)} fill={`url(#${id}b)`} />
        {clusters(bot + 24, H + 10)}
        {lipLeaves(bot + 8, -1)}
        {bright && <path d={`M-18 ${bot - 0.5} L18 ${bot - 0.5}`} stroke={lip} strokeWidth="1.6" opacity=".9" />}
      </g>
    );
  }
  // stars: a station's truss, a ring round the opening, a beacon on each collar.
  const panelAt = topLen > botLen ? top * 0.45 : bot + botLen * 0.55;
  return (
    <g transform={`translate(${x} 0)`}>
      <ellipse cx="0" cy={mid} rx="8" ry={(bot - top) / 2 + 3} fill="none" stroke={bright ? lip : skin.haze} strokeWidth="2" opacity=".32" />
      <rect x="-13" y="-4" width="26" height={Math.max(0, top - 6)} fill={`url(#${id}m)`} />
      <path d={zigzag(-11, 11, -4, top - 12, 13)} stroke="#d6e8f8" strokeOpacity=".35" strokeWidth="1" fill="none" />
      <rect x="-21" y={top - 11} width="42" height="11" rx="3" fill={`url(#${id}k)`} stroke="#0d1826" strokeWidth=".8" />
      <circle cx="0" cy={top - 5.5} r="6" fill={lip} opacity=".25" />
      <circle cx="0" cy={top - 5.5} r="2.3" fill={lip} />
      <rect x="-13" y={bot + 6} width="26" height={Math.max(0, botLen)} fill={`url(#${id}m)`} />
      <path d={zigzag(-11, 11, bot + 12, H + 4, 13)} stroke="#d6e8f8" strokeOpacity=".35" strokeWidth="1" fill="none" />
      <rect x="-21" y={bot} width="42" height="11" rx="3" fill={`url(#${id}k)`} stroke="#0d1826" strokeWidth=".8" />
      <circle cx="0" cy={bot + 5.5} r="6" fill={lip} opacity=".25" />
      <circle cx="0" cy={bot + 5.5} r="2.3" fill={lip} />
      {Math.max(topLen, botLen) > 80 && (
        <g transform={`translate(13 ${panelAt})`}>
          <path d="M0 0 L4 0" stroke="#9fb4c8" strokeWidth="2" />
          <rect x="4" y="-8" width="18" height="16" fill="#173a70" stroke="#6fa8e8" strokeWidth=".8" />
          <path d="M13 -8 L13 8 M4 0 L22 0" stroke="#6fa8e8" strokeWidth=".6" opacity=".7" />
        </g>
      )}
    </g>
  );
}

/** The flight, from its view state alone. */
function View({ view, era, accent, lang, input }: ViewProps<GapsView>) {
  const skin = ERA_SKIN[era];
  const id = useSvgId();
  const flown = view.f / W;
  const farShift = (view.f * 0.35) % W;
  // A demo glides every number between its keys: states and counts are read rounded.
  const state = Math.round(view.s);
  const waiting = state === 0 || state === 2;
  const status =
    state === 3
      ? { text: say(lang, '✓ THROUGH', '✓ GJENNOM'), tone: 'ok' as const }
      : view.sh > 0.5
        ? { text: say(lang, 'CLIPPED A GATE', 'TRAFF KANTEN'), count: [Math.round(view.n), view.of] as const, tone: 'bad' as const }
        : {
            text: say(lang, 'GATES', 'PORTER'),
            count: [Math.round(view.n), view.of] as const,
            hint: state === 0 ? say(lang, 'TAP TO FLY', 'TRYKK FOR Å FLY') : state === 2 ? say(lang, 'TAP TO FLY ON', 'TRYKK FOR Å FLY VIDERE') : undefined,
          };

  return (
    <BoardFrame era={era} accent={accent} status={status} fx={view.fx} input={input}>
      <defs>
        <linearGradient id={`${id}m`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#1d2a3a" />
          <stop offset=".35" stopColor="#7d93a8" />
          <stop offset=".6" stopColor="#4a5d72" />
          <stop offset="1" stopColor="#141e2a" />
        </linearGradient>
        <linearGradient id={`${id}k`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#e6eef6" />
          <stop offset="1" stopColor="#5a6b7c" />
        </linearGradient>
        <linearGradient id={`${id}s`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#10081c" />
          <stop offset=".4" stopColor="#2a1742" />
          <stop offset="1" stopColor="#0c0616" />
        </linearGradient>
        <pattern id={`${id}p`} width="12" height="11" patternUnits="userSpaceOnUse">
          <rect x="2" y="3" width="4" height="3" fill={skin.haze} opacity=".55" />
          <rect x="8" y="3" width="3" height="3" fill={skin.ink} opacity=".3" />
        </pattern>
        <linearGradient id={`${id}w`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#1a0c0d" />
          <stop offset=".4" stopColor="#5a3436" />
          <stop offset="1" stopColor="#1f0f10" />
        </linearGradient>
        <pattern id={`${id}z`} width="8" height="8" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
          <rect width="8" height="8" fill="#1a1208" />
          <rect width="4" height="8" fill="#e8b23a" />
        </pattern>
        <linearGradient id={`${id}r`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#2a1908" />
          <stop offset=".35" stopColor="#8a5a2a" />
          <stop offset=".7" stopColor="#5a3915" />
          <stop offset="1" stopColor="#1f1206" />
        </linearGradient>
        <linearGradient id={`${id}l`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#10260f" />
          <stop offset=".4" stopColor="#2f5e27" />
          <stop offset="1" stopColor="#0d200c" />
        </linearGradient>
        <linearGradient id={`${id}b`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#2a1a0c" />
          <stop offset=".5" stopColor="#6b4a2a" />
          <stop offset="1" stopColor="#24160a" />
        </linearGradient>
      </defs>

      {/* far away: the planet below the orbit, the city, the tunnel, the forest */}
      {era === 'stars' && <circle cx={W * 0.8} cy={H + 250} r={285} fill="#0b2a55" opacity=".55" stroke={skin.haze} strokeOpacity=".3" />}
      {SCATTER.slice(0, 16).map((p, i) => (
        <circle key={i} cx={((((p.x - flown * 0.2) % 1) + 1) % 1) * W} cy={p.y * H} r={p.r} fill={skin.haze} opacity={0.3 + (i % 3) * 0.15} />
      ))}
      {FAR[era] && (
        <g transform={`translate(${-farShift} 0)`} opacity={era === 'dust' ? 0.95 : 0.8}>
          <path d={FAR[era]} fill={era === 'neon' ? '#1a0d2c' : era === 'dust' ? '#2b1b0a' : '#0e2a10'} stroke={skin.ink} strokeOpacity=".22" />
          <path d={FAR[era]} transform={`translate(${W} 0)`} fill={era === 'neon' ? '#1a0d2c' : era === 'dust' ? '#2b1b0a' : '#0e2a10'} stroke={skin.ink} strokeOpacity=".22" />
        </g>
      )}

      {view.g.map(([x, c, s], i) => {
        const st = Math.round(s);
        return <GateArt key={i} era={era} id={id} x={x} top={c - view.gap / 2} bot={c + view.gap / 2} lip={st === 1 ? OK : st === 2 ? BAD : skin.haze} bright={st > 0} />;
      })}

      <g transform={`translate(${FLIGHT.shipX * W} ${view.y}) rotate(${view.a})`}>
        <ShipSide era={era} burn={view.b} shield={view.sh} />
      </g>
      {waiting && (
        <g transform={`translate(${FLIGHT.shipX * W} ${view.y - 30 + Math.sin(view.t / 190) * 3})`}>
          <path d="M-7 4 L0 -3 L7 4" stroke={accent} strokeWidth="2.4" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </g>
      )}
    </BoardFrame>
  );
}

/** How the ship looks after its last tap: the thruster flares, then settles. */
const burnAfter = (msSinceTap: number) => 0.25 + 0.75 * clamp(1 - msSinceTap / 320, 0, 1);
/** Its nose follows its climb or fall. */
const tiltOf = (vel: number) => clamp(vel * 34, -24, 20);

function Board(props: RoundProps) {
  const { level, lang } = props;
  const L = GAPS_LEVELS[level]!;
  const [gates] = useState<Gate[]>(() => lineUp(level, seeded(Date.now() + level * 7919)));
  const ship = useRef<Ship>({ y: 0.5, vel: 0 });
  const started = useRef(false);
  const lastTap = useRef(-1e9);
  const hitAt = useRef(-1e9);
  const passed = useRef(0);
  const flown = useRef(0);

  const round = useRound(props, (dt, now, won) => {
    const s = ship.current;
    if (won) {
      // Through the last gate: the ship levels out and the gates slide away.
      s.vel = 0;
      s.y += (0.5 - s.y) * Math.min(1, dt * 2);
      for (const g of gates) g.x -= L.speed * dt;
      flown.current += L.speed * dt;
      return;
    }
    if (!started.current) return;
    stepShip(s, dt);
    // Hands off the phone: the ship settles and the gates wait for the next tap.
    if (now - lastTap.current > FLIGHT.restMs) return;
    flown.current += L.speed * dt;
    const { clipped, flown: through, clean } = passGates(gates, L.speed * dt, s.y, L.gap);
    if (clipped > 0) {
      hitAt.current = now;
      round.miss();
    }
    if (through > 0) {
      if (clean > 0) round.hit();
      passed.current += through;
      round.progress(passed.current / L.gates);
      if (passed.current >= L.gates) round.win();
    }
  });

  const tap = () => {
    if (!round.live) return;
    started.current = true;
    lastTap.current = round.time();
    lift(ship.current);
  };

  const now = round.now;
  const s = ship.current;
  const view: GapsView = {
    t: q(now),
    y: q(s.y * H),
    a: round.won ? 0 : q(tiltOf(s.vel)),
    b: q(burnAfter(now - lastTap.current), 2),
    sh: q(clamp(1 - (now - hitAt.current) / 800, 0, 1), 2),
    g: gates.filter((g) => g.x * W > -40 && g.x * W < W + 40).map((g) => [q(g.x * W), q(g.c * H), g.hit ? 2 : g.passed ? 1 : 0] as const),
    gap: q(L.gap * H),
    f: q(flown.current * W),
    n: passed.current,
    of: L.gates,
    s: round.won ? 3 : !started.current ? 0 : now - lastTap.current > FLIGHT.restMs ? 2 : 1,
    fx: round.fx,
  };
  return <View view={round.show(view)} era={props.era} accent={props.accent} lang={lang} input={{ label: say(lang, 'Tap to fly up', 'Trykk for å fly opp'), down: tap }} />;
}

/**
 * The how-to, flown by the rules themselves: round 1's first two gates, the ship lifted by a steady
 * finger whenever it sinks below the next opening. The flight is worked out once, here, and keyed
 * every tenth of a second.
 */
function demoFlight(): DemoScript<GapsView> {
  const L = GAPS_LEVELS[0]!;
  const gates: Gate[] = [0.5, 0.36, 0.52].map((c, i) => ({ x: FLIGHT.firstGateX + i * L.spacing, c, passed: false, hit: false }));
  const ship: Ship = { y: 0.5, vel: 0 };
  const ms = 7_000;
  const firstTap = 1_000;
  const keys: DemoKey<GapsView>[] = [];
  const taps: number[] = [];
  let lastTap = -1e9;
  let flown = 0;
  let passed = 0;
  const dt = 1 / 60;
  for (let t = 0, frame = 0; t <= ms; t += dt * 1000, frame++) {
    if (t >= firstTap) {
      const next = gates.find((g) => !g.passed) ?? gates[gates.length - 1]!;
      if ((ship.y > next.c + 0.05 || taps.length === 0) && t - lastTap > 300) {
        lift(ship);
        lastTap = t;
        taps.push(Math.round(t));
      }
      stepShip(ship, dt);
      flown += L.speed * dt;
      passed += passGates(gates, L.speed * dt, ship.y, L.gap).flown;
    }
    if (frame % 6 === 0) {
      keys.push({
        at: Math.round(t),
        view: {
          t: 0,
          y: q(ship.y * H),
          a: q(tiltOf(ship.vel)),
          b: q(burnAfter(t - lastTap), 2),
          sh: 0,
          g: gates.map((g) => [q(g.x * W), q(g.c * H), g.passed ? 1 : 0] as const),
          gap: q(L.gap * H),
          f: q(flown * W),
          n: passed,
          of: L.gates,
          s: t < firstTap ? 0 : 1,
        },
      });
    }
  }
  const finger: FingerKey[] = [
    { at: 0, x: 262, y: 300, hide: true },
    { at: 300, x: 262, y: 300, hide: false },
    { at: 800, x: 246, y: 262 },
  ];
  for (const at of taps) finger.push({ at, x: 246, y: 262, down: true }, { at: at + 140, x: 246, y: 262, down: false });
  finger.push({ at: ms - 500, x: 262, y: 300, hide: true });
  return {
    ms,
    keys,
    finger,
    steps: [
      { at: 0, en: 'TAP TO FLY UP', no: 'TRYKK FOR Å FLY OPP' },
      { at: 1_900, en: 'LET GO AND IT SINKS', no: 'SLIPP, SÅ SYNKER DET' },
      { at: 3_400, en: 'FLY THROUGH THE GAPS', no: 'FLY GJENNOM ÅPNINGENE' },
    ],
  };
}

export const game = thirtyGame<GapsView>({
  Board,
  View,
  demo: demoFlight(),
  words: {
    en: {
      instruction: 'Tap to lift the ship, let it sink, and fly through the gaps. Three flights.',
      howMove: 'Tap anywhere to fly up. Between taps the ship sinks slowly.',
    },
    no: {
      instruction: 'Trykk for å løfte skipet, la det synke, og fly gjennom åpningene. Tre runder.',
      howMove: 'Trykk hvor som helst for å fly opp. Mellom trykkene synker skipet sakte.',
    },
  },
});
